/**
 * Filial-Stammdaten + Öffnungszeiten-Logik.
 *
 * Zuvor inline in branch-info.tsx; ausgelagert, weil jetzt sowohl die alte
 * (entfallene) Filial-Karte als auch die neue Sektion „Ihr Standort &
 * Ansprechpartner" (standort-ansprechpartner.tsx) darauf zugreifen.
 *
 * Die Keys (KAARST/DDORF) sind identisch zu den vom Backend gelieferten
 * `locationCodes` und zu den Schlüsseln in branch-employees.json.
 */

export interface BranchConfig {
  code: string;
  name: string;
  /** Hauptanschluss der Filiale (Durchwahl 0). */
  phone: string;
  /** Öffnungszeiten je Wochentag (0=Sonntag … 6=Samstag); null = geschlossen. */
  hours: Record<number, { open: number; close: number } | null>;
}

const WEEKDAY_HOURS = {
  0: null, // Sonntag — geschlossen
  1: { open: 7, close: 16 }, // Mo
  2: { open: 7, close: 16 }, // Di
  3: { open: 7, close: 16 }, // Mi
  4: { open: 7, close: 16 }, // Do
  5: { open: 7, close: 16 }, // Fr
  6: null, // Samstag — geschlossen
} as const;

export const BRANCHES: Record<string, BranchConfig> = {
  KAARST: {
    code: 'KAARST',
    name: 'Kaarst',
    phone: '02131/7909-0',
    hours: { ...WEEKDAY_HOURS },
  },
  DDORF: {
    code: 'DDORF',
    name: 'Düsseldorf',
    phone: '0211/91502-0',
    hours: { ...WEEKDAY_HOURS },
  },
};

const WEEKDAY_LABELS: Record<number, string> = {
  0: 'So',
  1: 'Mo',
  2: 'Di',
  3: 'Mi',
  4: 'Do',
  5: 'Fr',
  6: 'Sa',
};

const FULL_WEEKDAY_NAMES = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

/**
 * Verdichtet die Wochentags-Öffnungszeiten zu einem Anzeige-String,
 * z.B. "Mo–Fr 7:00–16:00". Gruppiert aufeinanderfolgende Tage mit
 * identischen Zeiten (Wochenstart Montag).
 */
export function formatHours(branch: BranchConfig): string {
  const order = [1, 2, 3, 4, 5, 6, 0];
  const groups: { from: number; to: number; open: number; close: number }[] = [];

  for (const day of order) {
    const h = branch.hours[day];
    if (!h) continue;
    const last = groups[groups.length - 1];
    if (last && last.open === h.open && last.close === h.close && order.indexOf(day) === order.indexOf(last.to) + 1) {
      last.to = day;
    } else {
      groups.push({ from: day, to: day, open: h.open, close: h.close });
    }
  }

  if (groups.length === 0) return 'Geschlossen';

  return groups
    .map((g) => {
      const days = g.from === g.to ? WEEKDAY_LABELS[g.from] : `${WEEKDAY_LABELS[g.from]}–${WEEKDAY_LABELS[g.to]}`;
      return `${days} ${g.open}:00–${g.close}:00`;
    })
    .join(', ');
}

export interface BranchStatus {
  isOpen: boolean;
  label: string;
  closingTime: string | null;
  openingTime: string | null;
}

/**
 * Aktueller Öffnungsstatus auf Basis der lokalen Uhrzeit.
 * Hinweis: nutzt `new Date()` — identisches Verhalten wie zuvor in branch-info.tsx.
 */
export function getBranchStatus(branch: BranchConfig): BranchStatus {
  const now = new Date();
  const day = now.getDay();
  const hour = now.getHours();
  const todayHours = branch.hours[day];

  const nextOpening = (): string | null => {
    for (let offset = 1; offset <= 7; offset++) {
      const nextDay = (day + offset) % 7;
      const nextHours = branch.hours[nextDay];
      if (nextHours) {
        return `${FULL_WEEKDAY_NAMES[nextDay]} ${nextHours.open}:00 Uhr`;
      }
    }
    return null;
  };

  if (!todayHours) {
    return { isOpen: false, label: 'Geschlossen', closingTime: null, openingTime: nextOpening() };
  }

  if (hour >= todayHours.open && hour < todayHours.close) {
    return { isOpen: true, label: 'Geöffnet', closingTime: `${todayHours.close}:00 Uhr`, openingTime: null };
  }

  if (hour < todayHours.open) {
    return { isOpen: false, label: 'Noch geschlossen', closingTime: null, openingTime: `${todayHours.open}:00 Uhr` };
  }

  // Nach Feierabend — nächste Öffnung suchen
  return { isOpen: false, label: 'Geschlossen', closingTime: null, openingTime: nextOpening() };
}

/**
 * Mappt einen einzelnen Filial-Code (NAV Global Dimension 1 Code des
 * Verkäufers, vom Backend bereits auf Uppercase normalisiert) auf die
 * zugehörige Filiale.
 *
 * Gibt `null` zurück, wenn kein Code vorliegt oder er keiner physischen
 * Filiale entspricht (z.B. `ORDERSHOP`, `WEB`). Aufrufer blenden in dem Fall
 * die Standort-/Ansprechpartner-Sektion aus — bewusst KEIN „beide Filialen"-
 * Fallback mehr (siehe standort-ansprechpartner.tsx).
 */
export function resolveBranch(code: string | undefined | null): BranchConfig | null {
  if (!code) return null;
  return BRANCHES[code] ?? null;
}
